
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { ApplicationError } from "@/lib/errors/types";

interface SignInFormProps {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  onForgotPasswordClick?: () => void;
}

export const SignInForm = ({ isSubmitting, setIsSubmitting, onForgotPasswordClick }: SignInFormProps) => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "L'email est requis";
    if (!emailRegex.test(email)) return "Format d'email invalide";
    return "";
  };

  const validatePassword = (password: string) => {
    if (!password) return "Le mot de passe est requis";
    if (password.length < 6) return "Le mot de passe doit contenir au moins 6 caractères";
    return "";
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validation
      const emailError = validateEmail(email);
      const passwordError = validatePassword(password);
      
      setFormErrors({
        email: emailError,
        password: passwordError,
      });

      if (emailError || passwordError) {
        setIsSubmitting(false);
        return;
      }

      // Nettoyage des données
      const sanitizedEmail = email.trim().toLowerCase();
      await signIn(sanitizedEmail, password);
      
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/welcome';
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Erreur lors de la connexion:", error);
      throw new ApplicationError(
        "Une erreur est survenue lors de la connexion",
        "error",
        { originalError: error }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFormErrors({ ...formErrors, email: "" });
          }}
          className={formErrors.email ? "border-red-500" : ""}
          required
          disabled={isSubmitting}
        />
        {formErrors.email && (
          <p className="text-red-500 text-sm">{formErrors.email}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="signin-password">Mot de passe</Label>
        <Input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setFormErrors({ ...formErrors, password: "" });
          }}
          className={formErrors.password ? "border-red-500" : ""}
          required
          disabled={isSubmitting}
        />
        {formErrors.password && (
          <p className="text-red-500 text-sm">{formErrors.password}</p>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Connexion en cours..." : "Se connecter"}
      </Button>
      {onForgotPasswordClick && (
        <div className="text-center mt-4">
          <Button
            type="button"
            variant="link"
            className="h-auto px-0 text-sm"
            onClick={onForgotPasswordClick}
          >
            Mot de passe oublié ?
          </Button>
        </div>
      )}
    </form>
  );
};
